import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircleHeart, ShieldCheck, Users, Search, Mail, Phone, MapPin, Clock } from "lucide-react";
import { api, fmtErr, trackEvent } from "../lib/api";
import SEO from "../components/SEO";

function Page({ title, eyebrow, children, seoDesc }) {
  return (
    <div className="px-4 sm:px-6 lg:px-12 max-w-3xl mx-auto py-12 md:py-20">
      <SEO title={`${title} | Tendly`} description={seoDesc} />
      <p className="text-xs uppercase tracking-widest font-semibold text-primary/80">{eyebrow}</p>
      <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight">{title}</h1>
      <div className="mt-8 space-y-6 text-muted-foreground leading-relaxed">{children}</div>
    </div>
  );
}

export function HowItWorks() {
  return (
    <Page title="How Tendly works" eyebrow="The process" seoDesc="How Tendly's free AI rental concierge finds your exact home in Vancouver — chat, match, tour.">
      {[
        [<MessageCircleHeart key="1" className="w-6 h-6" />, "1. Tell us what home feels like", "Open the live chat and answer a guided set of questions: neighbourhoods, radius, bedrooms, bathrooms, budget, furnished, parking, accessibility, pets, lease length, and move-in dates. It takes about four minutes, and you can go back and edit any answer."],
        [<Search key="2" className="w-6 h-6" />, "2. Get matched instantly", "Our matching engine compares your answers against real, de-duplicated listings aggregated from across the web. You see homes that match everything first, then similar homes with a match percentage."],
        [<Users key="3" className="w-6 h-6" />, "3. Humans book your viewings", "Select the homes you want to tour and share your weekly availability. A real Tendly agent contacts the properties and confirms your viewing schedule within 24 to 48 hours."],
      ].map(([icon, t, d]) => (
        <div key={t} className="rounded-2xl border border-border bg-card p-6">
          <span className="text-primary">{icon}</span>
          <h2 className="mt-3 font-heading font-semibold text-xl text-foreground">{t}</h2>
          <p className="mt-2 text-sm">{d}</p>
        </div>
      ))}
      <div className="rounded-2xl bg-primary text-primary-foreground p-6">
        <h2 className="font-heading font-semibold text-xl">Free for renters — always</h2>
        <p className="mt-2 text-sm text-primary-foreground/85">No fees, no marked-up rent, no catches. Just tell us what home feels like and we'll get to work.</p>
      </div>
      <Link to="/chat" data-testid="hiw-open-chat" className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3 font-semibold hover:bg-coral-600 transition-colors">
        <MessageCircleHeart className="w-5 h-5" /> Start your search
      </Link>
    </Page>
  );
}

export function About() {
  return (
    <Page title="About Tendly" eyebrow="Who we are" seoDesc="Tendly is an AI-powered rental concierge for long-term rentals in Vancouver, BC. Free for renters, always.">
      <p className="text-foreground text-lg font-medium">Tendly helps people find their exact home — free for renters, always.</p>
      <p>We started Tendly in Vancouver because rental search is broken: the same listings repeated across a dozen sites, ghost ads, and hours of doom-scrolling. Finding a home should feel like having a friend in the business — someone who listens, filters the noise, and books your tours.</p>
      <div className="rounded-2xl border border-border bg-card p-6">
        <h2 className="font-heading font-semibold text-lg text-foreground">Our mission</h2>
        <p className="mt-2 text-sm">Make renting in Vancouver honest and human. Every listing on Tendly is submitted or reviewed by our team before it goes live — no duplicates, no ghost ads, no bait pricing. Renters never pay us a cent: no fees, no marked-up rent, ever.</p>
      </div>
      <h2 className="font-heading font-semibold text-xl text-foreground">How we're different</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[
          [<MessageCircleHeart key="1" className="w-5 h-5" />, "A concierge, not a classifieds site", "Tell us what home feels like in a four-minute chat. Our matching weighs neighbourhood, budget, beds, pets, accessibility and more — and shows you the match score."],
          [<Search key="2" className="w-5 h-5" />, "Human-reviewed inventory", "Listings come from landlords and property managers directly, and our team reviews each one before it goes live. No ghost ads, no duplicates."],
          [<Users key="3" className="w-5 h-5" />, "Real humans close the loop", "AI does the searching; real agents confirm your viewings with the property and coordinate everything until you sign."],
          [<ShieldCheck key="4" className="w-5 h-5" />, "Private by design", "PIPEDA-compliant. We never sell your data, and your contact details are never shared without your consent."],
        ].map(([icon, t, d]) => (
          <div key={t} className="rounded-2xl border border-border bg-card p-5">
            <span className="w-10 h-10 rounded-xl bg-teal-50 dark:bg-teal-900/40 text-primary flex items-center justify-center">{icon}</span>
            <h3 className="mt-3 font-semibold text-foreground">{t}</h3>
            <p className="mt-1.5 text-sm">{d}</p>
          </div>
        ))}
      </div>
      <h2 className="font-heading font-semibold text-xl text-foreground">Where we work</h2>
      <p className="text-sm">We cover Vancouver — from Kitsilano (our home base) to Downtown, Mount Pleasant, the West End, Commercial Drive and beyond. <Link to="/listings" className="text-primary font-semibold hover:underline">Browse homes by neighbourhood</Link>.</p>
      <div className="rounded-2xl bg-primary text-primary-foreground p-6">
        <h2 className="font-heading font-semibold text-xl">Talk to a human</h2>
        <p className="mt-2 text-sm text-primary-foreground/85">Tendly · Kitsilano, BC, Canada<br />tendly.concierge@gmail.com · +1 (778) 956-6372</p>
        <Link to="/chat" data-testid="about-open-chat" className="mt-4 inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-2.5 text-sm font-semibold hover:bg-coral-600 transition-colors">
          <MessageCircleHeart className="w-4 h-4" /> Start your search
        </Link>
      </div>
    </Page>
  );
}

export function FAQ() {
  const [faqs, setFaqs] = useState([]);
  const [open, setOpen] = useState(0);
  useEffect(() => { api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => {}); }, []);
  return (
    <div className="px-4 sm:px-6 lg:px-12 max-w-3xl mx-auto py-12 md:py-20">
      <SEO title="FAQ — Tendly" description="Answers about Tendly's free AI rental concierge: how matching works, where listings come from, privacy, and more."
        jsonLd={{ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) }} />
      <p className="text-xs uppercase tracking-widest font-semibold text-primary/80">FAQ</p>
      <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight">Frequently asked questions</h1>
      <div className="mt-10 divide-y divide-border rounded-2xl border border-border bg-card overflow-hidden">
        {faqs.map((f, i) => (
          <div key={f.id || i}>
            <button data-testid={`faq-q-${i}`} onClick={() => setOpen(open === i ? -1 : i)} aria-expanded={open === i}
              className="w-full text-left px-6 py-5 font-semibold flex items-center justify-between gap-4 hover:bg-secondary/50 transition-colors">
              {f.q}<span className="text-primary shrink-0">{open === i ? "−" : "+"}</span>
            </button>
            {open === i && <p className="px-6 pb-6 text-sm text-muted-foreground leading-relaxed">{f.a}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function Privacy() {
  return (
    <Page title="Privacy Policy" eyebrow="PIPEDA compliant" seoDesc="Tendly's privacy policy: what we collect, why, your consent, and how to request deletion under PIPEDA.">
      <p className="text-sm">Last updated: June 2026</p>
      <h2 className="font-heading font-semibold text-xl text-foreground">What we collect</h2>
      <p className="text-sm">When you use the Tendly concierge, we collect your rental preferences (location, budget, bedrooms, move-in dates, and similar criteria) and — only with your explicit consent — your contact details (name, email, phone number) so our agents can coordinate viewings for you.</p>
      <h2 className="font-heading font-semibold text-xl text-foreground">How we use it</h2>
      <p className="text-sm">Your criteria are used to match you with rental listings and to coordinate viewings you request. We never sell your data. Your personal contact details are never shared in initial outreach to landlords — Tendly coordinates on your behalf.</p>
      <h2 className="font-heading font-semibold text-xl text-foreground">Consent</h2>
      <p className="text-sm">We ask for your explicit consent before collecting contact information, and we store that consent with a timestamp. You can withdraw consent at any time by emailing tendly.concierge@gmail.com.</p>
      <h2 className="font-heading font-semibold text-xl text-foreground">Your rights (PIPEDA)</h2>
      <p className="text-sm">You may request access to, correction of, or deletion of your personal information at any time. Deletion requests are processed within 30 days. Start one at <a href="/privacy/delete" className="text-primary underline">/privacy/delete</a>.</p>
      <DeleteRequestForm />
      <h2 className="font-heading font-semibold text-xl text-foreground">Contact</h2>
      <p className="text-sm">Privacy Officer, Tendly · Kitsilano, BC, Canada · tendly.concierge@gmail.com · +1 (778) 956-6372</p>
    </Page>
  );
}

function DeleteRequestForm() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const submit = async () => {
    try { await api.post("/privacy/delete-request", { email }); setDone(true); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <div className="rounded-2xl border border-border bg-card p-6">
      <h3 className="font-heading font-semibold text-foreground">Request data deletion</h3>
      {done ? (
        <p data-testid="delete-request-done" className="mt-2 text-sm">Almost done — we sent a verification link to that email. Click it to confirm your request.</p>
      ) : (
        <div className="mt-3 flex gap-2">
          <input data-testid="delete-request-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email address" aria-label="Email for deletion request"
            className="flex-1 rounded-xl border border-input bg-background px-4 py-2 text-sm" />
          <button data-testid="delete-request-submit" onClick={submit}
            className="rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-semibold">Submit</button>
        </div>
      )}
    </div>
  );
}

export function Terms() {
  return (
    <Page title="Terms of Service" eyebrow="Legal" seoDesc="Tendly terms of service.">
      <p className="text-sm">Last updated: June 2026</p>
      <h2 className="font-heading font-semibold text-xl text-foreground">The service</h2>
      <p className="text-sm">Tendly provides a rental concierge service: we present rental listings, match them against your stated preferences, and coordinate viewing requests with property managers and landlords. Tendly is free for renters.</p>
      <h2 className="font-heading font-semibold text-xl text-foreground">No guarantee of tenancy</h2>
      <p className="text-sm">Listings are provided by third parties; availability, pricing, and terms are set by the property owner or manager and may change without notice. Tendly does not guarantee any viewing, application outcome, or tenancy.</p>
      <h2 className="font-heading font-semibold text-xl text-foreground">Acceptable use</h2>
      <p className="text-sm">You agree to provide accurate information and not to misuse the service, attempt to scrape it, or misrepresent your identity.</p>
      <h2 className="font-heading font-semibold text-xl text-foreground">Contact</h2>
      <p className="text-sm">Tendly · Kitsilano, BC, Canada · tendly.concierge@gmail.com · +1 (778) 956-6372</p>
    </Page>
  );
}

export function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return toast.error("Please fill in all fields");
    setBusy(true);
    try { await api.post("/contact", form); setDone(true); } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };
  return (
    <Page title="Contact us" eyebrow="Say hello" seoDesc="Contact the Tendly team in Vancouver — email, phone, or send a message.">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          {[
            [Mail, "Email", "tendly.concierge@gmail.com", "mailto:tendly.concierge@gmail.com", "contact-email-link"],
            [Phone, "Phone", "+1 (778) 956-6372", "tel:+17789566372", "contact-phone-link"],
            [MapPin, "Office", "Kitsilano, Vancouver, BC", null, null],
            [Clock, "Response time", "Within one business day", null, null],
          ].map(([Icon, t, v, href, tid]) => (
            <div key={t} className="rounded-2xl border border-border bg-card p-5 flex gap-4 items-start">
              <span className="w-10 h-10 rounded-xl bg-teal-50 dark:bg-teal-900/40 text-primary flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></span>
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t}</p>
                {href ? <a href={href} data-testid={tid} className="font-semibold text-foreground hover:text-primary break-all">{v}</a>
                      : <p className="font-semibold text-foreground">{v}</p>}
              </div>
            </div>
          ))}
          <p className="text-xs text-muted-foreground">Looking for quick answers? Check the <Link to="/faq" className="text-primary font-semibold hover:underline">FAQ</Link> first.</p>
        </div>
        {done ? (
          <div data-testid="contact-done" className="rounded-2xl border border-border bg-card p-6 text-sm h-fit">
            <ShieldCheck className="w-5 h-5 text-primary mb-2" /> Thanks — we'll get back to you within one business day.
          </div>
        ) : (
          <div className="rounded-2xl border border-border bg-card p-6 space-y-3">
            <input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" aria-label="Your name"
              className="w-full rounded-xl border border-input bg-background px-4 py-2.5 min-h-[44px] text-sm" />
            <input data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" aria-label="Email"
              className="w-full rounded-xl border border-input bg-background px-4 py-2.5 min-h-[44px] text-sm" />
            <textarea data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} placeholder="How can we help?" aria-label="Message"
              className="w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm" />
            <button data-testid="contact-submit" onClick={submit} disabled={busy}
              className="rounded-full bg-primary text-primary-foreground px-6 py-2.5 min-h-[44px] text-sm font-semibold disabled:opacity-50">
              {busy ? "Sending…" : "Send message"}
            </button>
          </div>
        )}
      </div>
    </Page>
  );
}

export function PosterScan() {
  const { code } = useParams();
  useEffect(() => {
    trackEvent("page_view", { path: `/p/${code || "unknown"}` });
  }, [code]);
  return <Navigate to="/" replace />;
}
